import React, { useState, useMemo } from 'react';
import { X, MessageCircle } from 'lucide-react';

const BetaAnnouncementBar = ({ 
  // eslint-disable-next-line no-unused-vars
  showOnAllPages = true, 
  // eslint-disable-next-line no-unused-vars
  animation = 'marquee', 
  dismissDuration = 7 
}) => {
  // Calculate initial visibility
  const initialVisibility = useMemo(() => {
    const dismissedAt = localStorage.getItem('betaBarDismissed');
    const now = new Date().getTime();
    const dismissDurationMs = dismissDuration * 24 * 60 * 60 * 1000;
    return !dismissedAt || (now - parseInt(dismissedAt)) > dismissDurationMs;
  }, [dismissDuration]);

  const [isVisible, setIsVisible] = useState(initialVisibility);
  const [showModal, setShowModal] = useState(false);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('betaBarDismissed', new Date().getTime().toString());
  };

  const handleFeedbackClick = () => {
    setShowModal(true);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Beta Announcement Bar */}
      <div className="bg-black/95 border-b border-[#FFD700]/20 relative overflow-hidden beta-banner-container">
        <div className="max-w-7xl mx-auto px-4">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between gap-4 py-2">
            {/* Scrolling Text Container */}
            <div className="flex-1 overflow-hidden">
              <div className="animate-marquee whitespace-nowrap">
                <span className="text-white text-base">
                  🚀 You&apos;re part of the journey.{' '}
                  <span className="text-[#FFD700] font-bold">Mic Check Millionaire</span>{' '}
                  is currently in{' '}
                  <span className="text-[#FFD700] font-bold">Beta Mode</span> — 
                  features are evolving in real time. We appreciate your patience and feedback as we continue to grow. 💡
                </span>
              </div>
            </div>

            {/* Feedback Button */}
            <button
              onClick={handleFeedbackClick}
              className="flex-shrink-0 px-4 py-1.5 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black text-sm font-bold rounded transition-all duration-200 flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Give Feedback</span>
            </button>

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-white transition-colors"
              aria-label="Dismiss announcement"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col items-center justify-center text-center py-3 px-2 gap-2">
            {/* Static Text (No Marquee) */}
            <div className="text-white text-center leading-relaxed" style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>
              🚀 You&apos;re part of the journey.{' '}
              <span className="text-[#FFD700] font-bold">Mic Check Millionaire</span>{' '}
              is in{' '}
              <span className="text-[#FFD700] font-bold">Beta Mode</span>
            </div>
            
            {/* Buttons Row */}
            <div className="flex items-center gap-2 justify-center w-full">
              <button
                onClick={handleFeedbackClick}
                className="px-3 py-1.5 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold rounded-md transition-all duration-200 flex items-center gap-1.5 text-xs"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>Feedback</span>
              </button>
              
              {/* Dismiss Button */}
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-white transition-colors p-1"
                aria-label="Dismiss announcement"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showModal && (
        <FeedbackModal 
          onClose={() => setShowModal(false)} 
        />
      )}

      {/* Marquee Animation CSS */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        
        @media (max-width: 768px) {
          .animate-marquee {
            animation: none;
          }
        }
      `}</style>
    </>
  );
};

// Feedback Modal Component
const FeedbackModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Use relative URLs for same-origin requests (no CORS)
  const API_URL = '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/feedback/beta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          page_origin: window.location.pathname
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-black mb-2">
                💬 Help Us Improve Mic Check Millionaire
              </h2>
              <p className="text-gray-600">
                We&apos;re still building and your feedback makes a difference.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-[#FFD700] focus:outline-none transition"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-[#FFD700] focus:outline-none transition"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-[#FFD700] focus:outline-none transition h-32 resize-none"
                  placeholder="Share your feedback, bug reports, or feature ideas..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || !formData.message.trim()}
                className="w-full bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-bold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Feedback'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold text-black mb-2">
              Thank you for helping us build something legendary.
            </h3>
            <p className="text-gray-600">
              Your feedback has been received and will help shape the future of Mic Check Millionaire.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BetaAnnouncementBar;
